import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Collapse, Container,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel,
  MenuItem, Select, Slider, Stack, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, TextField, Typography
} from "@mui/material";
import {ChevronLeft, ChevronRight, ExpandLess, ExpandMore, SwapHoriz} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import SiteWrapper from "../SiteWrapper.react";
import {useAuthContext} from "../contexts/AuthenticationContext";
import TimeReclassificationService from "../services/timeReclassification.service";

type SiteSummary = {idSite: number | null; siteName: string; totalSeconds: number};
type ToolSummary = {
  idApplication: number;
  applicationName: string;
  browser: boolean;
  recordedSeconds: number;
  reservedSeconds: number;
  availableSeconds: number;
  sites: SiteSummary[];
};
type TaskSummary = {
  idProjectTask: number | null;
  taskName: string;
  idProject: number | null;
  projectName: string;
  totalSeconds: number;
  tools: ToolSummary[];
};
type DailySummary = {
  activityDate: string;
  earliestDate: string;
  latestDate: string;
  maxDays: number;
  totalSeconds: number;
  tasks: TaskSummary[];
};
type ToolTransfer = {idApplication: number; applicationName: string; requestedSeconds: number};
type RequestItem = {
  idTimeReclassificationRequest: number;
  requesterName?: string;
  activityDate: string;
  idSourceProjectTask?: number;
  sourceTaskName?: string;
  idSourceProject?: number;
  sourceProjectName?: string;
  idTargetProjectTask?: number;
  targetTaskName?: string;
  requestedSeconds: number;
  tools: ToolTransfer[];
  reason: string;
  status: string;
  requestedAt: string;
  reviewerName?: string;
  reviewComment?: string;
};

const dateString = (date: Date) => {
  const adjusted = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return adjusted.toISOString().slice(0, 10);
};

const yesterday = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return dateString(date);
};

const secondsLabel = (seconds: number) => {
  const safe = Math.max(0, Math.round(seconds || 0));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  return `${hours}h ${minutes.toString().padStart(2, "0")}m`;
};

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const summaryKey = (task: TaskSummary) => task.idProjectTask == null
  ? `unassigned-${task.idProject ?? "none"}`
  : `task-${task.idProjectTask}`;

export default function TimeReclassificationPage() {
  const {t} = useTranslation();
  const {user} = useAuthContext();
  const canReview = ["rLeader", "rAdmin"].includes(user?.role?.code);
  const service = useMemo(() => new TimeReclassificationService(), []);
  const [tab, setTab] = useState("daily");
  const [selectedDate, setSelectedDate] = useState(yesterday());
  const [summary, setSummary] = useState(null as DailySummary | null);
  const [loading, setLoading] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState([] as string[]);
  const [requestOpen, setRequestOpen] = useState(false);
  const [sourceTask, setSourceTask] = useState(null as TaskSummary | null);
  const [targetTask, setTargetTask] = useState("" as number | "");
  const [minutesByTool, setMinutesByTool] = useState({} as Record<number, number>);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useState([] as RequestItem[]);
  const [status, setStatus] = useState("ALL");
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionRequest, setDecisionRequest] = useState(null as RequestItem | null);
  const [comment, setComment] = useState("");

  const loadSummary = useCallback(() => {
    setLoading(true);
    setSummary(null);
    service.dailySummary(selectedDate)
      .then(({data}: any) => setSummary(data.data))
      .catch((error: any) => toast.error(apiError(error, t("time_reclassification.load_error"))))
      .finally(() => setLoading(false));
  }, [selectedDate, service, t]);

  const loadRequests = useCallback(() => {
    setLoading(true);
    const request = tab === "team" ? service.team(status) : service.mine(status);
    request.then(({data}: any) => setRequests(Array.isArray(data.data) ? data.data : []))
      .catch((error: any) => toast.error(apiError(error, t("time_reclassification.load_error"))))
      .finally(() => setLoading(false));
  }, [service, status, tab, t]);

  useEffect(() => {
    if (tab === "daily") loadSummary();
    else loadRequests();
  }, [loadRequests, loadSummary, tab]);

  const shiftDate = (days: number) => {
    const date = new Date(`${selectedDate}T12:00:00`);
    date.setDate(date.getDate() + days);
    const next = dateString(date);
    if ((!summary?.earliestDate || next >= summary.earliestDate) &&
      (!summary?.latestDate || next <= summary.latestDate)) setSelectedDate(next);
  };

  const openTransfer = (task: TaskSummary) => {
    setSourceTask(task);
    setTargetTask("");
    setReason("");
    const values: Record<number, number> = {};
    task.tools.forEach((tool: ToolSummary) => { values[tool.idApplication] = 0; });
    setMinutesByTool(values);
    setRequestOpen(true);
  };

  const destinationTasks = (summary?.tasks || []).filter((task: TaskSummary) =>
    task.idProjectTask != null && task.idProjectTask !== sourceTask?.idProjectTask &&
    (sourceTask?.idProject == null || task.idProject === sourceTask.idProject));
  const selectedTools = (sourceTask?.tools || []).filter((tool: ToolSummary) =>
    (minutesByTool[tool.idApplication] || 0) > 0);

  const submitRequest = () => {
    if (!sourceTask || targetTask === "" || !reason.trim() || !selectedTools.length) return;
    setSaving(true);
    service.create({
      activityDate: selectedDate,
      idSourceProjectTask: sourceTask.idProjectTask,
      idSourceProject: sourceTask.idProject,
      idTargetProjectTask: Number(targetTask),
      tools: selectedTools.map((tool: ToolSummary) => ({
        idApplication: tool.idApplication,
        requestedSeconds: minutesByTool[tool.idApplication] * 60
      })),
      reason: reason.trim()
    }).then(() => {
      toast.success(t("time_reclassification.request_created"));
      setRequestOpen(false);
      loadSummary();
    }).catch((error: any) => toast.error(apiError(error, t("time_reclassification.save_error"))))
      .finally(() => setSaving(false));
  };

  const cancelRequest = (request: RequestItem) => {
    if (!window.confirm(t("time_reclassification.cancel_confirm"))) return;
    service.cancel(request.idTimeReclassificationRequest).then(() => {
      toast.success(t("time_reclassification.cancelled"));
      loadRequests();
    }).catch((error: any) => toast.error(apiError(error, t("time_reclassification.save_error"))));
  };

  const decide = (decision: "APPROVE" | "REJECT") => {
    if (!decisionRequest || (decision === "REJECT" && !comment.trim())) {
      if (decision === "REJECT") toast.error(t("time_reclassification.rejection_required"));
      return;
    }
    setSaving(true);
    service.decide(decisionRequest.idTimeReclassificationRequest, decision, comment.trim())
      .then(() => {
        toast.success(t(decision === "APPROVE" ? "time_reclassification.approved" : "time_reclassification.rejected"));
        setDecisionOpen(false);
        loadRequests();
      }).catch((error: any) => toast.error(apiError(error, t("time_reclassification.save_error"))))
      .finally(() => setSaving(false));
  };

  const statusColor = (value: string): any => ({
    PENDING: "warning", APPROVED: "success", REJECTED: "error", CANCELLED: "default"
  } as any)[value] || "default";

  const toolList = (request: RequestItem) => request.tools?.length
    ? request.tools.map((tool: ToolTransfer) => `${tool.applicationName}: ${secondsLabel(tool.requestedSeconds)}`).join(", ")
    : t("time_reclassification.legacy_request");

  const taskLabel = (task: TaskSummary) => task.idProjectTask == null
    ? t("time_reclassification.unassigned_time")
    : task.taskName;

  const requestSourceLabel = (request: RequestItem) => request.idSourceProjectTask == null
    ? `${t("time_reclassification.unassigned_time")}${request.sourceProjectName ? ` · ${request.sourceProjectName}` : ""}`
    : request.sourceTaskName || "—";

  return <SiteWrapper>
    <Container maxWidth="xl" sx={{py: 4}}>
      <Stack direction="row" spacing={2} alignItems="center" mb={1}>
        <SwapHoriz color="primary" fontSize="large"/>
        <Typography variant="h4">{t("time_reclassification.title")}</Typography>
      </Stack>
      <Typography color="text.secondary" mb={3}>{t("time_reclassification.subtitle")}</Typography>
      <Tabs value={tab} onChange={(_: any, value: any) => {
        setStatus(value === "team" ? "PENDING" : "ALL"); setTab(value);
      }} sx={{mb: 3}}>
        <Tab value="daily" label={t("time_reclassification.daily")}/>
        <Tab value="mine" label={t("time_reclassification.mine")}/>
        {canReview && <Tab value="team" label={t("time_reclassification.team")}/>} 
      </Tabs>

      {tab === "daily" ? <>
        <Card variant="outlined" sx={{mb: 3}}><CardContent>
          <Stack direction={{xs: "column", sm: "row"}} alignItems={{sm: "center"}} justifyContent="space-between" spacing={2}>
            <Box><Typography variant="overline">{t("time_reclassification.date")}</Typography>
              <Stack direction="row" alignItems="center">
                <IconButton onClick={() => shiftDate(-1)} disabled={!!summary && selectedDate <= summary.earliestDate}><ChevronLeft/></IconButton>
                <TextField size="small" type="date" value={selectedDate}
                  inputProps={{min: summary?.earliestDate, max: summary?.latestDate}}
                  onChange={(event: any) => setSelectedDate(event.target.value)}/>
                <IconButton onClick={() => shiftDate(1)} disabled={!!summary && selectedDate >= summary.latestDate}><ChevronRight/></IconButton>
              </Stack>
            </Box>
            <Box textAlign={{sm: "right"}}><Typography variant="overline">{t("time_reclassification.daily_total")}</Typography>
              <Typography variant="h4" color="primary">{secondsLabel(summary?.totalSeconds || 0)}</Typography></Box>
          </Stack>
        </CardContent></Card>
        {loading ? <Box textAlign="center" py={6}><CircularProgress/></Box> :
          !summary?.tasks?.length ? <Alert severity="info">{t("time_reclassification.no_summary")}</Alert> :
            <Stack spacing={2}>{summary.tasks.map((task: TaskSummary) => {
              const key = summaryKey(task);
              const expanded = expandedTasks.includes(key);
              return <Card variant="outlined" key={key}><CardContent>
                <Stack direction={{xs: "column", sm: "row"}} spacing={2} alignItems={{sm: "center"}} justifyContent="space-between">
                  <Box flex={1}><Typography variant="h6">{taskLabel(task)}</Typography>
                    <Typography color="text.secondary">{task.projectName} · {secondsLabel(task.totalSeconds)}</Typography></Box>
                  <Stack direction="row" spacing={1}>
                    <IconButton onClick={() => setExpandedTasks(expanded
                      ? expandedTasks.filter((id: string) => id !== key)
                      : [...expandedTasks, key])}>{expanded ? <ExpandLess/> : <ExpandMore/>}</IconButton>
                    <Button variant="contained" startIcon={<SwapHoriz/>} disabled={!task.tools.some((tool: ToolSummary) => tool.availableSeconds >= 60) ||
                      !(summary.tasks || []).some((candidate: TaskSummary) => candidate.idProjectTask != null &&
                        candidate.idProjectTask !== task.idProjectTask && (task.idProject == null || candidate.idProject === task.idProject))}
                      onClick={() => openTransfer(task)}>{t("time_reclassification.transfer_time")}</Button>
                  </Stack>
                </Stack>
                <Collapse in={expanded}><Stack spacing={1.5} mt={2} pl={2} borderLeft={2} borderColor="divider">
                  {task.tools.map((tool: ToolSummary) => <Box key={tool.idApplication}>
                    <Stack direction="row" justifyContent="space-between"><Stack direction="row" spacing={1} alignItems="center">
                      <Typography>{tool.applicationName}</Typography>{tool.browser && <Chip size="small" label={t("time_reclassification.browser")}/>} 
                    </Stack><Typography>{secondsLabel(tool.recordedSeconds)} · {t("time_reclassification.available")}: {secondsLabel(tool.availableSeconds)}</Typography></Stack>
                    {!!tool.sites?.length && <Stack pl={2} mt={0.5}>{tool.sites.map((site: SiteSummary) =>
                      <Stack direction="row" justifyContent="space-between" key={`${tool.idApplication}-${site.idSite}-${site.siteName}`}>
                        <Typography variant="caption" color="text.secondary">{site.siteName}</Typography>
                        <Typography variant="caption" color="text.secondary">{secondsLabel(site.totalSeconds)}</Typography>
                      </Stack>)}</Stack>}
                  </Box>)}
                </Stack></Collapse>
              </CardContent></Card>;
            })}</Stack>}
      </> : <>
        <Stack direction="row" justifyContent="flex-end" mb={2}><FormControl size="small" sx={{minWidth: 180}}>
          <InputLabel>{t("time_reclassification.status")}</InputLabel>
          <Select label={t("time_reclassification.status")} value={status} onChange={(event: any) => setStatus(event.target.value)}>
            {(["ALL", "PENDING", "APPROVED", "REJECTED", "CANCELLED"] as const).map((value: string) =>
              <MenuItem key={value} value={value}>{t(`time_reclassification.status_${value.toLowerCase()}`)}</MenuItem>)}
          </Select></FormControl></Stack>
        {loading ? <Box textAlign="center" py={6}><CircularProgress/></Box> : !requests.length ?
          <Alert severity="info">{t("time_reclassification.no_requests")}</Alert> :
          <TableContainer component={Card} variant="outlined"><Table><TableHead><TableRow>
            {tab === "team" && <TableCell>{t("time_reclassification.requester")}</TableCell>}
            <TableCell>{t("time_reclassification.date")}</TableCell><TableCell>{t("time_reclassification.change")}</TableCell>
            <TableCell>{t("time_reclassification.tools")}</TableCell><TableCell>{t("time_reclassification.reason")}</TableCell>
            <TableCell>{t("time_reclassification.status")}</TableCell><TableCell align="right">{t("time_reclassification.actions")}</TableCell>
          </TableRow></TableHead><TableBody>{requests.map((request: RequestItem) =>
            <TableRow key={request.idTimeReclassificationRequest}>
              {tab === "team" && <TableCell>{request.requesterName || "—"}</TableCell>}
              <TableCell><Typography variant="body2">{request.activityDate}</Typography>
                <Typography variant="caption" color="text.secondary">{new Date(request.requestedAt).toLocaleString()}</Typography></TableCell>
              <TableCell>{requestSourceLabel(request)} → {request.targetTaskName || "—"}</TableCell>
              <TableCell>{toolList(request)}</TableCell><TableCell><Typography variant="body2">{request.reason}</Typography>
                {request.reviewComment && <Typography variant="caption" color="text.secondary" display="block">
                  {t("time_reclassification.review_comment")}: {request.reviewComment}{request.reviewerName ? ` — ${request.reviewerName}` : ""}
                </Typography>}</TableCell>
              <TableCell><Chip size="small" color={statusColor(request.status)} label={t(`time_reclassification.status_${request.status.toLowerCase()}`)}/></TableCell>
              <TableCell align="right">{tab === "mine" && request.status === "PENDING" &&
                <Button color="error" onClick={() => cancelRequest(request)}>{t("time_reclassification.cancel")}</Button>}
                {tab === "team" && request.status === "PENDING" && <Button onClick={() => {
                  setDecisionRequest(request); setComment(""); setDecisionOpen(true);
                }}>{t("time_reclassification.review")}</Button>}</TableCell>
            </TableRow>)}</TableBody></Table></TableContainer>}
      </>}
    </Container>

    <Dialog open={requestOpen} onClose={() => !saving && setRequestOpen(false)} fullWidth maxWidth="md">
      <DialogTitle>{t("time_reclassification.transfer_time")}</DialogTitle><DialogContent><Stack spacing={3} pt={1}>
        <Alert severity="info">{sourceTask ? taskLabel(sourceTask) : ""} · {sourceTask?.projectName}</Alert>
        <FormControl fullWidth required><InputLabel>{t("time_reclassification.destination_task")}</InputLabel>
          <Select label={t("time_reclassification.destination_task")} value={targetTask}
            onChange={(event: any) => setTargetTask(Number(event.target.value))}>
            {destinationTasks.map((task: TaskSummary) => <MenuItem value={task.idProjectTask as number} key={task.idProjectTask}>{task.taskName}</MenuItem>)}
          </Select></FormControl>
        <Typography variant="h6">{t("time_reclassification.tool_time_to_transfer")}</Typography>
        {(sourceTask?.tools || []).map((tool: ToolSummary) => {
          const maxMinutes = Math.floor(tool.availableSeconds / 60);
          const minutes = Math.min(minutesByTool[tool.idApplication] || 0, maxMinutes);
          return <Box key={tool.idApplication} px={1}><Stack direction="row" justifyContent="space-between">
            <Typography>{tool.applicationName}</Typography><Typography>{secondsLabel(minutes * 60)} / {secondsLabel(tool.availableSeconds)}</Typography>
          </Stack><Slider min={0} max={maxMinutes} step={1} value={minutes}
            onChange={(_: any, value: any) => setMinutesByTool({...minutesByTool, [tool.idApplication]: value as number})}
            valueLabelDisplay="auto" disabled={maxMinutes < 1}/></Box>;
        })}
        <TextField required multiline minRows={3} label={t("time_reclassification.reason")} value={reason}
          onChange={(event: any) => setReason(event.target.value)} inputProps={{maxLength: 1000}}/>
      </Stack></DialogContent><DialogActions>
        <Button onClick={() => setRequestOpen(false)} disabled={saving}>{t("time_reclassification.close")}</Button>
        <Button variant="contained" onClick={submitRequest} disabled={saving || targetTask === "" || !reason.trim() || !selectedTools.length}>
          {saving ? <CircularProgress size={20}/> : t("time_reclassification.submit")}</Button>
      </DialogActions></Dialog>

    <Dialog open={decisionOpen} onClose={() => !saving && setDecisionOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>{t("time_reclassification.review")}</DialogTitle><DialogContent><Stack spacing={2} pt={1}>
        <Alert severity="info">{decisionRequest?.requesterName}: {decisionRequest ? requestSourceLabel(decisionRequest) : "—"} → {decisionRequest?.targetTaskName || "—"}<br/>
          {decisionRequest ? toolList(decisionRequest) : ""}</Alert>
        <Typography>{decisionRequest?.reason}</Typography>
        <TextField multiline minRows={3} label={t("time_reclassification.review_comment")} value={comment}
          helperText={t("time_reclassification.rejection_help")} onChange={(event: any) => setComment(event.target.value)} inputProps={{maxLength: 1000}}/>
      </Stack></DialogContent><DialogActions>
        <Button onClick={() => setDecisionOpen(false)} disabled={saving}>{t("time_reclassification.close")}</Button>
        <Button color="error" variant="outlined" onClick={() => decide("REJECT")} disabled={saving}>{t("time_reclassification.reject")}</Button>
        <Button color="success" variant="contained" onClick={() => decide("APPROVE")} disabled={saving}>{t("time_reclassification.approve")}</Button>
      </DialogActions></Dialog>
  </SiteWrapper>;
}
